import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbs, useUser } from '../hooks';
import { Header, Table, Label, Groupper, Input, Button, Pagination, Icon, Toast, Modal, Field, Message } from 'react-frontier';
import { Link } from 'react-router-dom';
import { Expense, Department, ExpenseType } from '../SiteClasses';
import { addCommas, bindClick, getDepartmentName, formatCreatorName } from '../SiteUtil';
import { Dropdown } from 'semantic-ui-react';

import UserAccess from '../UserAccess';
import classNames from 'classnames';
import ExpenseModal from '../components/CreateExpenseModal';
import ApproveExpensesModal from '../components/ApproveExpensesModal';
import moment from 'moment';
import API from '../API';

const PAGE_SIZE = 20;

interface ExpensesListProps{
	
}

enum Modals{
	CREATE = 1,
	APPROVE = 2,
}

enum StatusFilter{
	PENDING = 0,
	APPROVED = 1,
	PAID = 2,
	REJECTED = 3,
}

var ExpensesList = (props: ExpensesListProps)=>{
	var { t } = useTranslation();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [expenses, setExpenses] = useState<Expense []>(null);
	var [input, setInput] = useState<string>('');
	var [searchExpenses, setSearchExpenses] = useState<Expense[]>(null);
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [searching, setSearching] = useState<boolean>(false);
	var [loading, setLoading] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [departmentFilters, setDepartmentFilters] = useState<Department[]>([]);
	var [typeFilter, setTypeFilter] = useState<number>(null);
	var [statusFilters, setStatusFilters] = useState<StatusFilter[]>([]);
	var [types, setTypes] = useState<ExpenseType[]>(null);
	var [dateStart, setDateStart] = useState<number>(null);
	var [dateEnd, setDateEnd] = useState<number>(null);
	var [dateModal, setDateModal] = useState<boolean>(false);
	var [dateModalPrompts, setDateModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		setTitle('Gastos');
		get();
		getTypes();
	}, []);

	useEffect(()=>{
		if(expenses !== null){
			setPage(0);
			get(0);
		}
	}, [departmentFilters, typeFilter, statusFilters]);

	var getTypes = ()=>{
		API.getExpenseTypes().then(res=>{
			if(res.error) return setLoadError(res.message);
			setTypes(res.data);
		}).catch(err=>{
			Toast.error('Hubo un error al cargar los tipos de gasto. (GLT-1)');
		});
	};

	var get = (get_page?: number)=>{
		var p = get_page ?? page;
		var offset = p * PAGE_SIZE;
		var startDate = dateStart ? moment.unix(dateStart).startOf('day').unix() : null;
		var endDate = dateEnd ? moment.unix(dateEnd).endOf('day').unix() : null;
		
		setLoading(true);
		API.getExpenses(PAGE_SIZE, offset, false, departmentFilters, typeFilter, statusFilters, startDate, endDate).then(res=>{
			if(res.error) return setLoadError(res.message);
			return setExpenses(res.data);
		}).catch(err => {
			return setLoadError('Hubo un error al obtener los gastos. (GES-1)');
		}).finally(()=>{
			setLoading(false);
		})
	};

	var onPageChange = (page: number)=>{
      setLoading(true)
		setPage(page);
		get(page);
	}

	var onSearchChange = (val: string)=>{
		setInput(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(val.length<3){
			return setSearchExpenses(null);
		}
		setSearchTimeout(setTimeout(()=>{
			search(val)
		}, 500));
	}

	var create = (newExpense: Expense)=>{
		setExpenses(prevExpenses => [newExpense, ...prevExpenses]);
		setShownModal(null);
	}

	var search = (query: string)=>{
		setSearching(true);
		setSearchExpenses(null);
		API.searchExpenses(query, statusFilters).then(res=>{
			if (res.error) return setLoadError(res.message);
			setSearchExpenses(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al buscar el gasto. (SES-1)');
		}).finally(()=>{
			setSearching(false);
		})
	}

	var onDateFilter = ()=>{
		if(!dateStart || !dateEnd) return setDateModalPrompts(['Debes seleccionar ambas fechas para filtrar']);
		if(dateEnd <= dateStart) return setDateModalPrompts(['La fecha de fin debe ser mayor que la fecha de inicio']);
		
		var startDate = moment.unix(dateStart).startOf('day').unix();
		var endDate = moment.unix(dateEnd).endOf('day').unix();
		
		setDateModal(false);
		setDateModalPrompts(null);
		setLoading(true);
		
		API.getExpenses(PAGE_SIZE, 0, false, departmentFilters, typeFilter, statusFilters, startDate, endDate).then(res=>{
			if(res.error) return setLoadError(res.message);
			setExpenses(res.data);
			setPage(0);
			setDateStart(startDate);
			setDateEnd(endDate);
		}).catch(err => {
			return setLoadError('Hubo un error al obtener los gastos. (GES-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onResetFilters = ()=>{
		setDateStart(null);
		setDateEnd(null);
		setLoading(true);
		API.getExpenses(PAGE_SIZE, 0, false, departmentFilters, typeFilter, statusFilters, null, null).then(res=>{
			if(res.error) return setLoadError(res.message);
			setExpenses(res.data);
			setPage(0);
		}).catch(err => {
			return setLoadError('Hubo un error al obtener los gastos. (GES-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};
	
	if(loadError) { return <Header text='Error' subtext={loadError} />};
	if(!expenses) { return <Header text='Cargando datos' loading/> };

	var shown_expenses = searchExpenses || expenses;

	return <div>
		<Groupper
			title={'Gastos'}
			titleRight={(
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button text='Opciones' color='black' iconRight='caret-down' iconRightStyle={{ marginRight: 0, marginLeft: 5 }} size='small'/>
				)}>
					<Dropdown.Menu>
						{hasAccess(UserAccess.EVENTS.CREATE_EXPENSE) && (
							<Dropdown.Item text={'Agregar'} icon={'plus'} onClick={bindClick(setShownModal, Modals.CREATE)}/>
						)}
						{hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) && (
							<Dropdown.Item text={'Aprobar'} icon={'check'} onClick={bindClick(setShownModal, Modals.APPROVE)}/>
						)}
						{hasAccess(UserAccess.ACCOUNTING.EDIT_EXPENSE_TYPE) && (
							<Dropdown.Item text={'Editar tipos'} icon={'list'} as={Link} to={'/expense/types'} />
						)}
						{hasAccess(UserAccess.ACCOUNTING.EDIT_REJECT_REASON) && (
							<Dropdown.Item text={'Razones de rechazo'} icon={'ban'} as={Link} to={'/expense/reject-reasons'} />
						)}
						{hasAccess(UserAccess.EVENTS.CREATE_EXPENSE) && (
							<Dropdown.Item text={'Métodos de pago'} icon={'credit card'} as={Link} to={'/expense/methods'} />
						)}
					</Dropdown.Menu>
				</Dropdown>
			)}
			style={{ marginTop: 20 }}
			width={1400}
		>
			<div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
				<Input placeholder='Buscar...' value={input} onChange={onSearchChange} style={{ flex: 1 }} />
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button 
						text='Tipo' 
						iconRight={typeFilter !== null ? 'asterisk' : null}
						iconRightStyle={{ color: 'brown', marginLeft: 5}}
						size='small'
						iconName='filter'
					/>
				)}>
					<Dropdown.Menu>
						<Dropdown.Item text='Limpiar filtro' icon='trash' onClick={() => setTypeFilter(null)} />
						{types && types.map(type => (
							<Dropdown.Item key={type.type_id} onClick={() => setTypeFilter(typeFilter === type.type_id ? null : type.type_id)}>
								<i className={classNames('icon circle', {
									'check': typeFilter === type.type_id,
									'outline': typeFilter !== type.type_id,
								})}></i>
								{type.type_name}
							</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button 
						text='Filtros' 
						iconRight={(departmentFilters.length > 0 || statusFilters.length > 0) ? 'asterisk' : null}
						iconRightStyle={{ color: 'brown', marginLeft: 5}}
						size='small'
						iconName='filter'
					/>
				)}>
					<Dropdown.Menu>
						<Dropdown.Item text='Limpiar todos' icon='trash' onClick={() => {
							setDepartmentFilters([]);
							setStatusFilters([]);
						}} />
						<Dropdown.Header content='Departamentos' />
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.OPERACIONES) 
									? prev.filter(d => d !== Department.OPERACIONES)
									: [...prev, Department.OPERACIONES]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.OPERACIONES),
								'square outline': !departmentFilters.includes(Department.OPERACIONES),
							})}></i>
							Operaciones
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.COMERCIAL) 
									? prev.filter(d => d !== Department.COMERCIAL)
									: [...prev, Department.COMERCIAL]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.COMERCIAL),
								'square outline': !departmentFilters.includes(Department.COMERCIAL),
							})}></i>
							Comercial
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.DESARROLLO) 
									? prev.filter(d => d !== Department.DESARROLLO)
									: [...prev, Department.DESARROLLO]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.DESARROLLO),
								'square outline': !departmentFilters.includes(Department.DESARROLLO),
							})}></i>
							Desarrollo
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.RECURSOS_HUMANOS) 
									? prev.filter(d => d !== Department.RECURSOS_HUMANOS)
									: [...prev, Department.RECURSOS_HUMANOS]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.RECURSOS_HUMANOS),
								'square outline': !departmentFilters.includes(Department.RECURSOS_HUMANOS),
							})}></i>
							Recursos Humanos
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.MARKETING) 
									? prev.filter(d => d !== Department.MARKETING)
									: [...prev, Department.MARKETING]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.MARKETING),
								'square outline': !departmentFilters.includes(Department.MARKETING),
							})}></i>
							Marketing
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.CONTABILIDAD) 
									? prev.filter(d => d !== Department.CONTABILIDAD)
									: [...prev, Department.CONTABILIDAD]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.CONTABILIDAD),
								'square outline': !departmentFilters.includes(Department.CONTABILIDAD),
							})}></i>
							Contabilidad
						</Dropdown.Item>
						<Dropdown.Divider />
						<Dropdown.Header content='Estatus' />
						<Dropdown.Item onClick={() => {
							setStatusFilters(prev => 
								prev.includes(StatusFilter.PENDING) 
									? prev.filter(s => s !== StatusFilter.PENDING)
									: [...prev, StatusFilter.PENDING]
							);
						}}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(StatusFilter.PENDING),
								'square outline': !statusFilters.includes(StatusFilter.PENDING),
							})}></i>
							Pendiente
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setStatusFilters(prev => 
								prev.includes(StatusFilter.APPROVED) 
									? prev.filter(s => s !== StatusFilter.APPROVED)
									: [...prev, StatusFilter.APPROVED]
							);
						}}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(StatusFilter.APPROVED),
								'square outline': !statusFilters.includes(StatusFilter.APPROVED),
							})}></i>
							Aprobado
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setStatusFilters(prev => 
								prev.includes(StatusFilter.PAID) 
									? prev.filter(s => s !== StatusFilter.PAID)
									: [...prev, StatusFilter.PAID]
							);
						}}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(StatusFilter.PAID),
								'square outline': !statusFilters.includes(StatusFilter.PAID),
							})}></i>
							Pagado
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setStatusFilters(prev => 
								prev.includes(StatusFilter.REJECTED) 
									? prev.filter(s => s !== StatusFilter.REJECTED)
									: [...prev, StatusFilter.REJECTED]
							);
						}}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(StatusFilter.REJECTED),
								'square outline': !statusFilters.includes(StatusFilter.REJECTED),
							})}></i>
							Rechazado
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button 
						text='Fecha' 
						iconRight={(dateStart && dateEnd) ? 'asterisk' : null}
						iconRightStyle={{ color: 'brown', marginLeft: 5}}
						size='small'
						iconName='calendar'
					/>
				)}>
					<Dropdown.Menu>
						<Dropdown.Item icon='calendar' text='Filtrar por fecha' onClick={bindClick(setDateModal, true)} />
						<Dropdown.Item icon='close' text='Borrar filtro' onClick={() => onResetFilters()} />
					</Dropdown.Menu>
				</Dropdown>
			</div>
			<Table striped fitted selectable
				className='first last'
				style={{ marginTop: 15 }}
				headers={['ID', 'Creador', 'Titulo', 'Tipo', 'Departamento', 'Creado', 'Estatus', 'Monto']}
				collapsingIndexes={[0, 1, 2, 3, 4, 5, 6, 7]}
				footer={(!!shown_expenses && shown_expenses.length >= PAGE_SIZE || page !== 0) && (
					<Table.Cell colSpan={9} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={shown_expenses.length>=PAGE_SIZE} />
						</div>
					</Table.Cell>
				)}
			>
				{searching || loading ? (
				<Table.Cell row className='empty' colSpan={8}>
						<Header loading text='Cargando datos' />
					</Table.Cell>
				) : (
					shown_expenses.length !== 0 ? (
						shown_expenses.map(e => (
							<Table.Row
								compact
								key={e.expense_id}
								as={Link}
								to={`/expenses/${e.expense_id}`}
							collapsingIndexes={[0, 1, 2, 3, 4, 5, 6, 7]}
							data={[
								e.expense_id,
								formatCreatorName(e.user_creator_name),
									e.expense_title,
									e.type_name,
									e.department_name ? <Label style={{ width: 80, textAlign: 'center', padding: 3, fontSize: 11 }} color='orange'>{getDepartmentName(e.department_id)}</Label> : <Icon name='minus'/>,
									moment.unix(e.date_created).format('DD/MM/YY HH:mm'),
									<Label style={{ width: 80, textAlign: 'center', padding: '2px 4px', fontSize: 11 }} color={e.paid ? 'green' : e.approved ? 'blue' : (e.rejected && !e.paid) ? 'red' : 'yellow'}>
										{e.paid ? 'Pagado' : e.approved ? 'Aprobado' : (e.rejected && !e.paid) ? 'Rechazado' : 'Pendiente'}
									</Label>,
									'$' + addCommas(e.amount),
								]}
							/>
						))
					) : (
					<Table.Cell row className='empty' colSpan={8}>
						No se encontraron registros
					</Table.Cell>
					)
				)}
			</Table>
		</Groupper>
		<ExpenseModal
			open={shownModal === Modals.CREATE}
			onClose={()=> setShownModal(null)}
			onCreate={(e) => create(e)}
		/>
		<ApproveExpensesModal
			open={shownModal === Modals.APPROVE}
			onClose={bindClick(setShownModal, null)}
			onComplete={(approvedIds) => {
				setExpenses(prevExpenses => prevExpenses.map(e => 
					approvedIds.includes(e.expense_id) ? { ...e, approved: true } : e
				));
				if(searchExpenses) {
					setSearchExpenses(prevSearch => prevSearch.map(e => 
						approvedIds.includes(e.expense_id) ? { ...e, approved: true } : e
					));
				}
				setShownModal(null);
			}}
		/>
		<Modal open={dateModal} size='mini' onClose={bindClick(setDateModal, false)}>
			<Modal.Header>Filtrar por fecha</Modal.Header>
			<Modal.Content>
				<Field amount={2}>
					<Input 
						label='Fecha inicio' 
						value={dateStart} 
						onChange={setDateStart}
						style={{ width: '100%'}}
						calendar={{ 
							date: moment().startOf('isoWeek').unix(), 
							mode: 'date', 
							format: 'DD/MM/YYYY' 
						}} 
					/>
					<Input 
						label='Fecha fin' 
						value={dateEnd}
						style={{ width: '100%'}}
						onChange={setDateEnd} 
						calendar={{ 
							date: moment().endOf('isoWeek').unix(), 
							mode: 'date', 
							format: 'DD/MM/YYYY' 
						}} 
					/>
				</Field>
				<Message type="error" list={dateModalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setDateModal, false)} />
				<Button text='Guardar' color='black' onClick={onDateFilter} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default ExpensesList;